import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import {
    BsDownload,
    BsGeoAltFill,
    BsLinkedin,
    BsPatchCheckFill,
    BsPersonWorkspace,
    BsTelephoneFill
} from "react-icons/bs";
import "./Contact.css";

const web3FormsEndpoint = "https://api.web3forms.com/submit";
const successOverlayDuration = 2500;
const successOverlayFadeDuration = 420;

const contactLinks = [
    {
        label: "LinkedIn",
        value: "Dimos Nikolakopoulos",
        href: "https://www.linkedin.com/in/dimos-nikolakopoulos/",
        icon: <BsLinkedin />,
        external: true
    },
    {
        label: "Phone",
        value: "+1 (646) 409 0705",
        href: "tel:+16464090705",
        icon: <BsTelephoneFill />
    }
];

const contactHighlights = [
    {
        label: "Availability",
        value: "Open to software engineering conversations",
        icon: <BsPatchCheckFill />
    },
    {
        label: "Location",
        value: "NYC / Northern New Jersey metro area",
        icon: <BsGeoAltFill />
    },
    {
        label: "Best fit",
        value: "React, TypeScript, full-stack delivery",
        icon: <BsPersonWorkspace />
    }
];

function Contact() {
    const accessKey = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY ?? "";
    const [status, setStatus] = useState("idle");
    const [statusMessage, setStatusMessage] = useState("");
    const [overlayStatus, setOverlayStatus] = useState("hidden");
    const overlayTimerRef = useRef(null);

    const canSubmit = useMemo(() => accessKey.trim().length > 0, [accessKey]);
    const isSubmitLocked = overlayStatus !== "hidden";

    useEffect(() => {
        return () => {
            if (overlayTimerRef.current) {
                window.clearTimeout(overlayTimerRef.current);
            }
        };
    }, []);

    const handleDownload = () => {
        const link = document.createElement("a");
        link.href = "/files/Dimostenis_Nikolakopoulos_Resume.pdf";
        link.download = "Dimostenis_Nikolakopoulos_Resume.pdf";
        link.click();
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (isSubmitLocked) {
            return;
        }

        if (overlayTimerRef.current) {
            window.clearTimeout(overlayTimerRef.current);
            overlayTimerRef.current = null;
        }

        const form = event.currentTarget;
        const formData = new FormData(form);
        const reason = formData.get("reason") || "Portfolio message";

        if (!canSubmit) {
            setStatus("error");
            setStatusMessage("The contact form is ready, but not connected yet.");
            return;
        }

        setStatus("sending");
        setStatusMessage("Sending your message...");
        setOverlayStatus("sending");

        formData.append("access_key", accessKey);
        formData.append("from_name", "Dimostenis Portfolio");
        formData.append("subject", `Portfolio - ${reason}`);

        try {
            const response = await fetch(web3FormsEndpoint, {
                method: "POST",
                body: formData,
            });
            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || "Message could not be sent.");
            }

            form.reset();
            setStatus("success");
            setStatusMessage("Message sent successfully.");
            setOverlayStatus("success");
            overlayTimerRef.current = window.setTimeout(() => {
                setOverlayStatus("success-exiting");
                overlayTimerRef.current = window.setTimeout(() => {
                    setOverlayStatus("hidden");
                    overlayTimerRef.current = null;
                }, successOverlayFadeDuration);
            }, successOverlayDuration - successOverlayFadeDuration);
        } catch (error) {
            setStatus("error");
            setStatusMessage(
                error instanceof Error
                    ? error.message
                    : "Something went wrong while sending the message."
            );
            setOverlayStatus("hidden");
        }
    };

    return (
        <Box id="contactContainer" className="fade-in">
            <section className="contact-shell" aria-labelledby="contact-title">
                <Box className="contact-intro">
                    <Typography component="p" className="contact-kicker">
                        Contact
                    </Typography>
                    <Typography component="h1" id="contact-title" className="contact-title">
                        Let&apos;s talk about the next build.
                    </Typography>
                    <Typography component="p" className="contact-copy">
                        I&apos;m available for software engineering conversations, project work, and roles focused on practical React, TypeScript, and full-stack delivery.
                    </Typography>
                    <Button
                        type="button"
                        variant="contained"
                        startIcon={<BsDownload />}
                        onClick={handleDownload}
                        className="contact-resume-button"
                    >
                        Download Resume
                    </Button>

                    <Box className="contact-highlight-grid" aria-label="Contact context">
                        {contactHighlights.map((item) => (
                            <Box className="contact-highlight" key={item.label}>
                                <Box className="contact-highlight-icon" aria-hidden="true">
                                    {item.icon}
                                </Box>
                                <Box>
                                    <Typography component="span" className="contact-highlight-label">
                                        {item.label}
                                    </Typography>
                                    <Typography component="span" className="contact-highlight-value">
                                        {item.value}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box className="contact-stack">
                    <Box className="contact-card-list" aria-label="Contact methods">
                        {contactLinks.map((item) => (
                            <Box
                                key={item.label}
                                component="a"
                                href={item.href}
                                target={item.external ? "_blank" : undefined}
                                rel={item.external ? "noreferrer" : undefined}
                                className="contact-card"
                            >
                                <Box className="contact-card-icon" aria-hidden="true">
                                    {item.icon}
                                </Box>
                                <Box>
                                    <Typography component="span" className="contact-card-label">
                                        {item.label}
                                    </Typography>
                                    <Typography component="span" className="contact-card-value">
                                        {item.value}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    <form className="contact-form" onSubmit={handleSubmit}>
                        {overlayStatus !== "hidden" && (
                            <div
                                className={`contact-form-overlay is-${overlayStatus}`}
                                aria-live="polite"
                                aria-busy={overlayStatus === "sending"}
                            >
                                <div className="contact-form-overlay-content">
                                    {overlayStatus === "sending" ? (
                                        <>
                                            <span className="contact-form-spinner" aria-hidden="true" />
                                            <span>Sending your message...</span>
                                        </>
                                    ) : (
                                        <span>Message sent successfully.</span>
                                    )}
                                </div>
                            </div>
                        )}

                        <input
                            className="contact-botcheck"
                            type="checkbox"
                            name="botcheck"
                            tabIndex="-1"
                            autoComplete="off"
                        />

                        <Typography component="p" className="contact-form-kicker">
                            Message
                        </Typography>

                        <label>
                            <span>Name</span>
                            <input type="text" name="name" autoComplete="name" required maxLength="80" />
                        </label>

                        <label>
                            <span>Email</span>
                            <input type="email" name="email" autoComplete="email" required maxLength="120" />
                        </label>

                        <label>
                            <span>Reason</span>
                            <select name="reason" defaultValue="Portfolio message">
                                <option>Portfolio message</option>
                                <option>Project inquiry</option>
                                <option>Role opportunity</option>
                            </select>
                        </label>

                        <label>
                            <span>Message</span>
                            <textarea name="message" required minLength="12" rows="6" />
                        </label>

                        <div className="contact-actions">
                            <p className={`contact-status is-${status}`} aria-live="polite">
                                {statusMessage}
                            </p>

                            <button type="submit" disabled={isSubmitLocked}>
                                {isSubmitLocked ? "Sending" : "Send Message"}
                            </button>
                        </div>
                    </form>
                </Box>
            </section>
        </Box>
    );
}

export default Contact;
